/**
 * Dealer detail with Suspend/Reinstate actions. Suspend is a
 * maker-checker permission (see canonical.ts); the operator may need a
 * supervisor's approval upstream, but the controller permits direct calls
 * when the user holds the permission (platform_admin, risk_officer).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import { useState } from "react";
import { ApiError, type Dealer } from "@dfp/api-client";
import { useAuth } from "../auth/AuthContext.js";
import { Badge } from "../components/Badge.js";
import { Button } from "../components/Button.js";
import { Card, CardBody, CardHeader, CardTitle } from "../components/Card.js";
import { EditDealerForm } from "../components/EditDealerForm.js";
import { RequestApprovalForm } from "../components/RequestApprovalForm.js";
import { api } from "../lib/api.js";

export function DealerDetailPage(): JSX.Element {
  const { id } = useParams({ strict: false }) as { id: string };
  const auth = useAuth();
  const queryClient = useQueryClient();
  const [reason, setReason] = useState("");
  const [showReasonInput, setShowReasonInput] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const dealerQuery = useQuery({
    queryKey: ["dealers", "byId", id],
    queryFn: () => api.get<Dealer>(`/api/dealers/${id}`),
  });

  const suspend = useMutation({
    mutationFn: (r: string) => api.post<Dealer>(`/api/dealers/${id}/suspend`, { reason: r }),
    onSuccess: (next) => {
      queryClient.setQueryData<Dealer>(["dealers", "byId", id], next);
      void queryClient.invalidateQueries({ queryKey: ["dealers", "list"] });
      setReason("");
      setShowReasonInput(false);
      setError(null);
    },
    onError: (err: unknown) => {
      setError(formatError(err));
    },
  });

  const reinstate = useMutation({
    mutationFn: () => api.post<Dealer>(`/api/dealers/${id}/reinstate`),
    onSuccess: (next) => {
      queryClient.setQueryData<Dealer>(["dealers", "byId", id], next);
      void queryClient.invalidateQueries({ queryKey: ["dealers", "list"] });
      setError(null);
    },
    onError: (err: unknown) => {
      setError(formatError(err));
    },
  });

  if (dealerQuery.isLoading) return <p className="text-sm text-slate-500">Loading…</p>;
  if (dealerQuery.isError || !dealerQuery.data) {
    return <p className="text-sm text-red-700">Failed to load dealer.</p>;
  }
  const d = dealerQuery.data;
  const suspended = d.suspendedAt != null;
  const permitted = (perm: string): boolean => (auth.user?.permissions ?? []).includes(perm);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold text-slate-900">{d.name}</h1>
        <p className="text-xs font-mono text-slate-500">{d.id}</p>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Status</CardTitle>
            <Badge tone={suspended ? "danger" : "success"}>
              {suspended ? "suspended" : "active"}
            </Badge>
          </CardHeader>
          <CardBody className="space-y-2 text-sm">
            <Row k="Risk tier" v={d.riskTier} />
            <Row k="Stock limit" v={String(d.stockLimit)} />
            <Row k="Created" v={new Date(d.createdAt).toLocaleDateString()} />
            {suspended ? (
              <div className="mt-2 rounded-md bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-800">
                <strong>Suspended at:</strong> {new Date(d.suspendedAt as string).toLocaleString()}
                {d.suspendedReason ? <div className="mt-1">{d.suspendedReason}</div> : null}
              </div>
            ) : null}
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Actions</CardTitle>
          </CardHeader>
          <CardBody className="space-y-3">
            <EditDealerForm dealer={d} />

            {!suspended && permitted("dealer.suspend") ? (
              <>
                {showReasonInput ? (
                  <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
                    <label className="block">
                      <span className="text-xs font-medium text-slate-700">
                        Reason for suspension (≥ 10 chars)
                      </span>
                      <textarea
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        rows={3}
                        className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                      />
                    </label>
                    <div className="mt-2 flex gap-2">
                      <Button
                        variant="danger"
                        onClick={() => suspend.mutate(reason.trim())}
                        disabled={suspend.isPending || reason.trim().length < 10}
                      >
                        Confirm suspension
                      </Button>
                      <Button
                        variant="ghost"
                        onClick={() => {
                          setShowReasonInput(false);
                          setReason("");
                        }}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Button variant="danger" onClick={() => setShowReasonInput(true)}>
                    Suspend dealer …
                  </Button>
                )}
              </>
            ) : null}

            {suspended && permitted("dealer.write") ? (
              <Button onClick={() => reinstate.mutate()} disabled={reinstate.isPending}>
                {reinstate.isPending ? "Reinstating…" : "Reinstate dealer"}
              </Button>
            ) : null}

            {!suspended && permitted("iam.approval.create") ? (
              <RequestApprovalForm
                permissionKey="dealer.suspend"
                label="suspend dealer"
                variant="danger"
                payloadBuilder={(reason) => ({ dealerId: d.id, reason })}
              />
            ) : null}

            {!permitted("dealer.suspend") &&
            !permitted("dealer.write") &&
            !permitted("iam.approval.create") ? (
              <p className="text-xs text-slate-500">
                You don't have permission to change this dealer's status.
              </p>
            ) : null}

            {error ? (
              <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700">
                {error}
              </div>
            ) : null}
          </CardBody>
        </Card>
      </div>
    </div>
  );
}

function Row({ k, v }: { k: string; v: string }): JSX.Element {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <span className="text-xs uppercase tracking-wide text-slate-500">{k}</span>
      <span className="text-sm font-medium text-slate-900">{v}</span>
    </div>
  );
}

function formatError(err: unknown): string {
  if (err instanceof ApiError) {
    return err.problem.message ?? err.problem.detail ?? "Request rejected";
  }
  return "Network error";
}
